import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Building,
  Settings,
  BriefcaseBusiness,
  ExternalLink,
  Sparkles,
} from 'lucide-react';

import {
  Sidebar,
  SidebarHeader,
  SidebarContent,
  SidebarFooter,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarGroup,
  SidebarGroupLabel,
} from '@/components/ui/sidebar';

import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { useCompany } from '@/contexts/CompanyContext';

const DashboardSidebar: React.FC = () => {
  const location = useLocation();
  const { isTenantInterviewer } = useAuth();
  const { selectedCompany } = useCompany();

  // Helper to check if a path is active
  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  // Get job board URL for the selected company
  const jobBoardUrl = selectedCompany?.handle ? `/jobs/${selectedCompany.handle}` : null;

  return (
    <Sidebar>
      <SidebarHeader className="h-14 p-0">
        <div className="flex h-full items-center px-4">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo-32.png" alt="HireMetrics Logo" className="h-8 w-8" />
            <div className="font-semibold text-lg">InterviewAI</div>
          </Link>
        </div>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Overview</SidebarGroupLabel>
          <SidebarMenu className="px-2">
            <SidebarMenuItem>
              <Link to="/dashboard">
                <SidebarMenuButton
                  isActive={isActive('/dashboard')}
                  tooltip="Dashboard"
                  className={cn(
                    'w-full h-12 text-base transition-all duration-200 hover:bg-accent hover:text-accent-foreground hover:scale-[1.02] active:scale-[0.98] rounded-lg relative',
                    isActive('/dashboard') &&
                      'bg-primary/10 text-primary border-l-4 border-primary font-semibold shadow-sm'
                  )}
                >
                  <LayoutDashboard className="h-5 w-5 transition-transform duration-200 group-hover:scale-110" />
                  <span className="font-medium">Dashboard</span>
                </SidebarMenuButton>
              </Link>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel>Management</SidebarGroupLabel>
          <SidebarMenu className="px-2 space-y-1">
            <SidebarMenuItem>
              <Link to="/companies">
                <SidebarMenuButton
                  isActive={isActive('/companies')}
                  tooltip="Companies"
                  className={cn(
                    'w-full h-12 text-base transition-all duration-200 hover:bg-accent hover:text-accent-foreground hover:scale-[1.02] active:scale-[0.98] rounded-lg group relative',
                    isActive('/companies') &&
                      'bg-primary/10 text-primary border-l-4 border-primary font-semibold shadow-sm'
                  )}
                >
                  <Building className="h-5 w-5 transition-transform duration-200 group-hover:scale-110" />
                  <span className="font-medium">Companies</span>
                </SidebarMenuButton>
              </Link>
            </SidebarMenuItem>

            <SidebarMenuItem>
              <Link to="/positions">
                <SidebarMenuButton
                  isActive={isActive('/positions')}
                  tooltip="Positions"
                  className={cn(
                    'w-full h-12 text-base transition-all duration-200 hover:bg-accent hover:text-accent-foreground hover:scale-[1.02] active:scale-[0.98] rounded-lg group relative',
                    isActive('/positions') &&
                      'bg-primary/10 text-primary border-l-4 border-primary font-semibold shadow-sm'
                  )}
                >
                  <BriefcaseBusiness className="h-5 w-5 transition-transform duration-200 group-hover:scale-110" />
                  <span className="font-medium">Positions</span>
                </SidebarMenuButton>
              </Link>
            </SidebarMenuItem>

            <SidebarMenuItem>
              <Link to="/candidates">
                <SidebarMenuButton
                  isActive={isActive('/candidates')}
                  tooltip="Candidates"
                  className={cn(
                    'w-full h-12 text-base transition-all duration-200 hover:bg-accent hover:text-accent-foreground hover:scale-[1.02] active:scale-[0.98] rounded-lg group relative',
                    isActive('/candidates') &&
                      'bg-primary/10 text-primary border-l-4 border-primary font-semibold shadow-sm'
                  )}
                >
                  <Users className="h-5 w-5 transition-transform duration-200 group-hover:scale-110" />
                  <span className="font-medium">Candidates</span>
                </SidebarMenuButton>
              </Link>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroup>

        {/* Only show Settings for non-interviewer users */}
        {!isTenantInterviewer && (
          <SidebarGroup>
            <SidebarGroupLabel>System</SidebarGroupLabel>
            <SidebarMenu className="px-2">
              <SidebarMenuItem>
                <Link to="/settings">
                  <SidebarMenuButton
                    isActive={isActive('/company-settings')}
                    tooltip='Settings'
                    className={cn(
                      'w-full h-12 text-base transition-all duration-200 hover:bg-accent hover:text-accent-foreground hover:scale-[1.02] active:scale-[0.98] rounded-lg group relative',
                      isActive('/company-settings') &&
                        'bg-primary/10 text-primary border-l-4 border-primary font-semibold shadow-sm'
                    )}
                  >
                    <Settings className="h-5 w-5 transition-transform duration-200 group-hover:scale-110" />
                    <span className="font-medium">Settings</span>
                  </SidebarMenuButton>
                </Link>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroup>
        )}
      </SidebarContent>

      <SidebarFooter className="p-4">
        {/* Job Board Link - visible to all users */}
        {jobBoardUrl && (
          <div className="mb-4">
            <a href={jobBoardUrl} target="_blank" rel="noopener noreferrer" className="block">
              <SidebarMenuButton
                tooltip="View Your Public Job Board"
                className={cn(
                  'w-full h-12 text-base transition-all duration-200 hover:bg-blue-600 hover:text-white hover:scale-[1.02] active:scale-[0.98] rounded-lg group relative',
                  'text-blue-600'
                )}
              >
                <Sparkles className="h-5 w-5 transition-transform duration-200 group-hover:scale-110" />
                <span className="font-medium">Your Job Board</span>
                <ExternalLink className="h-4 w-4 ml-auto opacity-50 group-hover:opacity-100 transition-opacity" />
              </SidebarMenuButton>
            </a>
          </div>
        )}
        <div className="flex items-center justify-center text-xs text-muted-foreground">
          <span>v{__APP_VERSION__}</span>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
};

export default DashboardSidebar;
